import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { useParams } from 'react-router-dom'
import { Box } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useGetCustomersWalletQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import {
  currentCustomerAtom,
  currentCustomerWalletAtom,
} from 'pages/Customers/atoms/customersAtoms'
import { WalletDetails } from 'pages/WalletDetails/WalletDetails'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys, reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { CustomerWalletDetails, NestedCustomerDetails } from 'types/customers'
import { WalletTypes } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import CustomerWalletPageHeader from './components/CustomerWallet/CustomerWalletPageHeader'
import { WalletActionButtons } from './components/WalletActionButtons'
import { CustomerWalletPageParams } from './types'

export const NestedCustomerWallet = () => {
  const queryClient = useQueryClient()
  const { customerReference, customerWalletId }: CustomerWalletPageParams = useParams()

  const { profile } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)

  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)
  const setCurrentCustomerWalletAtom = useSetAtom(currentCustomerWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)

  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(null)
  const [isPayWalletModalOpen, setIsPayWalletModalOpen] = useState(false)

  const { data: customer, isFetching: isFetchingCustomer } =
    useCustomerQuery<NestedCustomerDetails>(customerReference)
  const { data: customerWallet, isFetching: isFetchingCustomerWallet } = useGetCustomersWalletQuery(
    customerWalletId,
    !!customerWalletId
  )

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (customerWallet?.id) {
      return getWalletActionCapabilityRequestFilters([customerWallet?.id])
    }
  }, [customerWallet?.id])

  const {
    data: bulkCapabilities,
    isFetching: isFetchingCapabilities,
    isError: isErrorFetchingCapabilities,
  } = useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  useEffect(() => {
    setCurrentCustomerAtom(customer)
    return () => {
      setCurrentCustomerAtom(null)
    }
  }, [customer])

  useEffect(() => {
    setCurrentCustomerWalletAtom(customerWallet)
    return () => {
      setCurrentCustomerWalletAtom(null)
    }
  }, [customerWallet])

  const { data: walletId, isFetching: isFetchingWalletLegacyId } = useGetWalletLegacyId(
    customerWallet?.id
  )

  const isLoading = useMemo(() => {
    return isFetchingCustomerWallet ?? isFetchingCustomer ?? isFetchingWalletLegacyId
  }, [isFetchingCustomerWallet, isFetchingCustomer, isFetchingWalletLegacyId])

  const customerWalletDetails: CustomerWalletDetails = useMemo(() => {
    return {
      ...customerWallet,
      legacyWalletId: walletId,
    }
  }, [walletId, customerWallet])

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
    await queryClient.invalidateQueries({
      queryKey: [reactQueryName.CUSTOMER_WALLETS],
    })
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
    })
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  return (
    <>
      <PageHeader
        description={
          <CustomerWalletPageHeader customerWallet={customerWallet} isLoading={isLoading} />
        }
        renderRightSection={
          <Box flex gapX={12}>
            <WalletActionButtons
              customer={customer}
              customerWallet={customerWallet}
              bulkCapabilities={bulkCapabilities}
              isFetchingCapabilities={isFetchingCapabilities}
              isFetchingWalletLegacyId={isFetchingWalletLegacyId}
              setIsSheetOpen={setIsSheetOpen}
              setWalletModalAction={setWalletModalAction}
              setIsPayWalletModalOpen={setIsPayWalletModalOpen}
              cleanupUnifiedPayoutsFlow={cleanupUnifiedPayoutsFlow}
            />
          </Box>
        }
      />
      <Box flexCol gapX={24} gapY={24} width="full">
        <WalletDetails customerWalletDetails={customerWalletDetails} />
      </Box>

      <WalletSheet isSheetOpen={isSheetOpen} />

      {isPayWalletModalOpen && (
        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={refreshWallets}
          setShowWalletModal={setIsPayWalletModalOpen}
        />
      )}
    </>
  )
}
